import { useCallback, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent, MouseEvent as ReactMouseEvent } from 'react';

interface AgentPanelDrag {
    lastPointerX: number;
    lastPointerY: number;
}

/**
 * Dragging logic for repositioning agent panels in world-space.
 * Converts screen-space pointer deltas to world-space using the current zoom level.
 */
export function useAgentPanelDrag(onPositionChange: (agentId: string, position: { x: number; y: number }) => void, zoomLevel: number) {
    const [drag, setDrag] = useState<AgentPanelDrag | null>(null);
    const agentIdRef = useRef<string | null>(null);
    const positionRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

    const startDrag = useCallback(
        (event: ReactPointerEvent<HTMLDivElement>, agentId: string, currentPosition: { x: number; y: number }) => {
            event.stopPropagation();
            event.preventDefault();
            agentIdRef.current = agentId;
            positionRef.current = currentPosition;
            setDrag({
                lastPointerX: event.clientX,
                lastPointerY: event.clientY,
            });

            // Capture pointer on the target so we get move events even outside the element
            (event.target as HTMLElement).setPointerCapture(event.pointerId);
        },
        [],
    );

    const updateDrag = useCallback(
        (event: ReactMouseEvent<HTMLDivElement> | ReactPointerEvent<HTMLDivElement>) => {
            if (!drag || !agentIdRef.current) return;

            const deltaX = (event.clientX - drag.lastPointerX) / zoomLevel;
            const deltaY = (event.clientY - drag.lastPointerY) / zoomLevel;

            const newPosition = {
                x: positionRef.current.x + deltaX,
                y: positionRef.current.y + deltaY,
            };
            positionRef.current = newPosition;
            onPositionChange(agentIdRef.current, newPosition);

            setDrag({
                lastPointerX: event.clientX,
                lastPointerY: event.clientY,
            });
        },
        [drag, zoomLevel, onPositionChange],
    );

    const endDrag = useCallback(() => {
        setDrag(null);
        agentIdRef.current = null;
    }, []);

    return {
        isDragging: drag !== null,
        startDrag,
        updateDrag,
        endDrag,
    };
}
